import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import type { GridColDef } from '@mui/x-data-grid';
import {
  AddUserIcon,
  DataTable,
  LockIcon,
  ResetPasswordIcon,
  SectionCard,
  StatusChip,
  UnlockIcon,
  UsersIcon,
} from '@platform/ui-kit';
import { TALOGENT_DOMAIN, api, type AdminUser } from '../../api';
import { describeError, useAsyncData } from '../admin/useAsyncData';

/**
 * Talogent Users — the only place talogent.com accounts are listed.
 *
 * Same shape as the customer's Users screen, deliberately: an administrator should
 * not have to learn two different tables. What differs is scope — this reads a
 * Talogent-only endpoint, and there is no module or seat assignment, because vendor
 * staff do not consume the client's licences.
 */
/** Same treatment as the customer Users screen — one visual language. */
const ACTION_SX = { color: 'text.secondary', '&:hover': { color: 'primary.main' } } as const;

export function TalogentUsersTab() {
  const users = useAsyncData(() => api.admin.getTalogentUsers());
  const [adding, setAdding] = useState(false);
  const [resetTarget, setResetTarget] = useState<AdminUser>();
  const [actionError, setActionError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(undefined);
    try {
      await fn();
      users.reload();
    } catch (err) {
      setActionError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const columns: GridColDef<AdminUser>[] = [
    { field: 'full_name', headerName: 'Name', flex: 1, minWidth: 160 },
    { field: 'email', headerName: 'Email', flex: 1.3, minWidth: 220 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => {
        const row = params.row;
        if (row.is_active !== 1) return <StatusChip tone="neutral" label="Inactive" />;
        if (row.is_locked === 1) return <StatusChip tone="error" label="Locked" />;
        if (row.force_password_change === 1) return <StatusChip tone="warning" label="Must reset" />;
        return <StatusChip tone="success" label="Active" />;
      },
    },
    {
      field: 'last_login',
      headerName: 'Last sign-in',
      width: 180,
      valueFormatter: (value) => (value ? new Date(value as string).toLocaleString() : 'Never'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row">
          <Tooltip title={params.row.is_locked === 1 ? 'Unlock' : 'Lock'}>
            <IconButton
              size="small"
              disabled={busy}
              sx={ACTION_SX}
              onClick={() =>
                run(() =>
                  params.row.is_locked === 1
                    ? api.admin.unlockUser(params.row.user_id)
                    : api.admin.lockUser(params.row.user_id),
                )
              }
            >
              {params.row.is_locked === 1 ? (
                <UnlockIcon fontSize="small" />
              ) : (
                <LockIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset password">
            <IconButton
              size="small"
              sx={ACTION_SX}
              onClick={() => setResetTarget(params.row)}
            >
              <ResetPasswordIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (users.loading) return <CircularProgress />;
  if (users.error) return <Alert severity="error">{users.error}</Alert>;

  return (
    <Stack spacing={2}>
      {actionError ? (
        <Alert severity="error" onClose={() => setActionError(undefined)}>
          {actionError}
        </Alert>
      ) : null}

      <SectionCard
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <UsersIcon fontSize="small" />
            <span>Talogent users</span>
          </Stack>
        }
        subheader={`Vendor staff only. Accounts here must use an @${TALOGENT_DOMAIN} address and do not appear in the customer's user list.`}
        action={
          <Button
            variant="contained"
            startIcon={<AddUserIcon />}
            onClick={() => setAdding(true)}
          >
            Add Talogent user
          </Button>
        }
        disableContentPadding
      >
        <DataTable
          rows={users.data?.users ?? []}
          columns={columns}
          getRowId={(row) => row.user_id}
          pageSizeOptions={[10, 25]}
        />
      </SectionCard>

      {adding ? (
        <AddTalogentUserDialog
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            users.reload();
          }}
        />
      ) : null}

      {resetTarget ? (
        <ResetDialog
          user={resetTarget}
          onClose={() => setResetTarget(undefined)}
          onDone={() => {
            setResetTarget(undefined);
            users.reload();
          }}
        />
      ) : null}
    </Stack>
  );
}

function AddTalogentUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const domainOk = email.trim().toLowerCase().endsWith(`@${TALOGENT_DOMAIN}`);

  const save = async () => {
    setSaving(true);
    setError(undefined);
    try {
      // Role is fixed: this screen only ever creates vendor staff.
      await api.admin.createUser({
        fullName,
        email,
        password,
        role: 'Talogent Administrator',
      });
      onCreated();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Talogent user</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="Full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            error={email.length > 0 && !domainOk}
            helperText={
              email.length > 0 && !domainOk
                ? `Must be an @${TALOGENT_DOMAIN} address.`
                : `Talogent staff only — @${TALOGENT_DOMAIN}.`
            }
          />
          <TextField
            label="Initial password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            helperText="At least 12 characters with upper case, lower case and a number. They must change it at first sign-in."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || !fullName.trim() || !domainOk || !password}
        >
          {saving ? 'Creating…' : 'Create user'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ResetDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser;
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async () => {
    setSaving(true);
    setError(undefined);
    try {
      await api.admin.resetPassword(user.user_id, password);
      onDone();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Reset password for {user.full_name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            autoFocus
            helperText="The account is unlocked and must be changed at next sign-in."
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving || !password}>
          {saving ? 'Resetting…' : 'Reset password'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
